import { useState, useCallback } from 'react';
import { post, get } from '../utils/api';

export function useTransactions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false
  });

  /**
   * Record a new transaction
   * @param {Object} transactionData - Transaction data
   * @param {string} transactionData.txSignature - Transaction signature
   * @param {string} transactionData.walletAddress - User's wallet address
   * @param {'send'|'receive'} transactionData.type - Transaction type
   * @param {number} transactionData.amount - Transaction amount
   * @param {string} transactionData.tokenMint - Token mint address
   * @param {string} [transactionData.destinationWallet] - Destination wallet (for sends)
   * @param {string} [transactionData.status] - Transaction status
   * @param {Object} [transactionData.metadata] - Additional metadata
   * @returns {Promise<{success: boolean, transaction?: Object, error?: string}>}
   */
  const recordTransaction = useCallback(async (transactionData) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await post('/api/transactions', transactionData);
      
      if (response.success) {
        // Add the new transaction to the local state
        setTransactions(prev => [response.transaction, ...prev]);
        
        // Update pagination total
        setPagination(prev => ({
          ...prev,
          total: prev.total + 1
        }));
      }
      
      return response;
    } catch (err) {
      const errorMsg = err.message || 'Failed to record transaction';
      setError(errorMsg);
      console.error('Error recording transaction:', err);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch transactions for a wallet
   * @param {string} walletAddress - Wallet address to fetch transactions for
   * @param {Object} [options] - Query options
   * @param {number} [options.limit] - Number of transactions to fetch
   * @param {number} [options.offset] - Offset for pagination
   * @param {'send'|'receive'} [options.type] - Filter by transaction type
   * @param {string} [options.tokenMint] - Filter by token mint address
   * @param {boolean} [options.append] - Whether to append to existing transactions
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  const fetchTransactions = useCallback(async (walletAddress, {
    limit = 50,
    offset = 0,
    type,
    tokenMint,
    append = false
  } = {}) => {
    if (!walletAddress) {
      setError('Wallet address is required');
      return { success: false, error: 'Wallet address is required' };
    }

    setLoading(true);
    setError(null);
    
    try {
      const queryParams = new URLSearchParams();
      if (limit) queryParams.append('limit', limit);
      if (offset) queryParams.append('offset', offset);
      if (type) queryParams.append('type', type);
      if (tokenMint) queryParams.append('tokenMint', tokenMint);

      const url = `/api/transactions/${walletAddress}?${queryParams.toString()}`;
      const response = await get(url);
      
      if (response.success) {
        setTransactions(prev => 
          append ? [...prev, ...response.data] : response.data
        );
        
        setPagination({
          total: response.pagination.total,
          limit: response.pagination.limit,
          offset: response.pagination.offset,
          hasMore: response.pagination.hasMore
        });
      }
      
      return response;
    } catch (err) {
      const errorMsg = err.message || 'Failed to fetch transactions';
      setError(errorMsg);
      console.error('Error fetching transactions:', err);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Load more transactions (pagination)
   * @returns {Promise<void>}
   */
  const loadMoreTransactions = useCallback(async (walletAddress, filters = {}) => {
    if (!pagination.hasMore || loading) return;
    
    const newOffset = pagination.offset + pagination.limit;
    return fetchTransactions(walletAddress, {
      ...filters,
      offset: newOffset,
      append: true
    });
  }, [pagination, loading, fetchTransactions]);

  return {
    transactions,
    loading,
    error,
    pagination,
    recordTransaction,
    fetchTransactions,
    loadMoreTransactions,
    clearError: () => setError(null)
  };
}

export default useTransactions;
